import Container from "@/components/Container/Container";
import Layout from "@/components/Layout/Layout";
import { H5Css, Heading } from "@/components/Typography";
import { darkColor, mutedBlueColor } from "@/constants/colors";
import { cookiesPath, termsPath } from "@/constants/paths";
import { css } from "@emotion/react";
import { GetStaticProps } from "next";

const Page = () => {
  return (
    <Layout
      title="Privacy Policy"
      metaDescription="Privacy Policy"
      heroContainerCss={css`
        background: url(/images/policies/BG.svg), ${mutedBlueColor};
        background-size: cover;
        background-repeat: no-repeat;
      `}
      hero={
        <Container>
          <Heading
            level={1}
            css={css`
              color: ${darkColor};
              text-align: center;
              margin-top: 40px;
              padding-bottom: 87px;
              ${H5Css}
            `}
          >
            Privacy Policy
          </Heading>
        </Container>
      }
    >
      <Container
        css={css`
          padding: 60px 30px;
        `}
      >
        <div>
          <style>{`
          table,
          th,
          td {
            border: 1px solid black;
          }
        `}</style>
          <div>
            <p style={{ textAlign: "center" }}>
              Digital Edge Sport, Inc. d/b/a Jersey Watch
            </p>
            <p style={{ textAlign: "center" }}>
              Website and Services Privacy Policy
            </p>
            <p>Last Revised: May 2, 2022</p>
            <p>
              <strong>1. General</strong>
              <br />
              <br />
              We know that your privacy is important to you, and we work hard to
              earn and keep your trust. Digital Edge Sport, Inc. d/b/a Jersey
              Watch (collectively with its subsidiaries, “Jersey Watch,” the
              “Company,” “we,” “us,” and “our,”) respects your privacy and is
              committed to protecting your privacy through our compliance with
              this Privacy Policy (the “Policy”).
            </p>
            <p>This Policy describes:</p>
            <ul>
              <li>
                The types of information we collect from you or that you may
                provide when you visit our website available at:{" "}
                <a href="https://www.jerseywatch.com/">
                  https://www.jerseywatch.com/
                </a>{" "}
                as well as any websites and blogs directly owned by the Company
                (collectively, our “Website”) and any applications, services,
                and tools (including, but not limited to, building websites,
                creating online registrations, posting and sending announcements
                and events, adding website administrators and purchasing a
                Jersey Watch plan) (collectively, our “Services”) provided on
                the Website.
              </li>
              <li>
                Our practices for collecting, using, maintaining, protecting,
                and disclosing that information.
              </li>
            </ul>
            <p>
              This Policy governs our interactions with you in any one of the
              following capacities:
            </p>
            <ul>
              <li>
                <strong>
                  <u>Administrator</u>
                </strong>
                . Our customers who have access to our Website and Services as
                an individual or organization to create a website, set up online
                registration, post and send announcements, add events and
                additional Administrators and select a subscription plan. Such
                individuals may include, but are not limited to, coaches, team
                managers, youth league organizers, parents of individual players
                and so on. As such, you may be granted an ID and password to
                access secure portals on our Website. You may also be
                responsible for uploading Personal Information.
              </li>
              <li>
                <strong>
                  <u>End User</u>
                </strong>
                . The General Users (as defined below) and visitors of an
                Administrators&apos; website (“Administrator Site” or “Site”).
                Such users may include but are not limited to the parents or
                legal guardians of athletes and players.
              </li>
              <li>
                <strong>
                  <u>General User</u>
                </strong>
                <strong>.</strong> You may simply visit our Website and browse
                information, possibly sign up for a newsletter, create an
                account or otherwise request communications from the Company.
              </li>
            </ul>
            <p>
              Unless otherwise specified, this Policy applies in the same manner
              to you and your Personal Information.
            </p>
            <p>
              This Policy applies to information we collect on this Website or
              in emails and other electronic messages between you and this
              Website, and information gathered when you interact with our
              advertising on third-party websites if such advertisements include
              links to this Policy.
            </p>
            <p>
              This Policy does not apply to information collected by us offline
              or through any other means, including on any other website
              operated by the Company or any third party, or information
              collected by any third party through any application or content
              (including advertising) that may link to or be accessible from the
              Website (for further information, see below, “Third-party
              Websites”).
              <br />
              <br />
              Please read this Policy carefully to understand our practices
              regarding your information and how we will treat it. If you do not
              agree with our policies and practices, then please do not use our
              Website. By using our Website, you agree to the terms of this
              Policy. This Policy may change from time to time (see below,
              “Changes to this Policy”). Your continued use of our Website after
              we make changes is deemed to be acceptance of those changes, so
              please check the Policy periodically for updates.
            </p>
            <p>
              <strong>2. Website Terms of Use; Other Agreements</strong>
            </p>
            <p>
              This Policy should be read in conjunction with any applicable{" "}
              <a href={termsPath}>Terms of Use</a>, into which this Policy is
              incorporated by reference. Furthermore, as an Administrator or
              General User, your use of the Website and our processing of your
              information may also be subject to a terms of sale or other
              agreement specific to the nature of any transaction in which you
              are participating.
            </p>
            <p>
              <strong>3. What We Collect and How We Collect It</strong>
            </p>
            <p>
              To ensure that we provide you with the best possible experience,
              we will store, use, and share personal information about you in
              accordance with this Policy. Personal information is information
              that identifies, relates to, describes, references, is reasonably
              capable of being associated with, or could reasonably be linked,
              directly or indirectly, with a particular user, household or
              device (“Personal Information”). In particular, the Website has
              collected the following categories of Personal Information from
              users of the Website within the last 12 months:
            </p>
            <table cellPadding={8} cellSpacing={0}>
              <tbody>
                <tr>
                  <td valign="top">
                    <p>Category</p>
                  </td>
                  <td valign="top">
                    <p>Examples</p>
                  </td>
                  <td valign="top">
                    <p>Collected</p>
                  </td>
                </tr>
                <tr>
                  <td valign="top">
                    <p>Identifiers.</p>
                  </td>
                  <td valign="top">
                    <p>
                      A real name, postal address, unique personal identifier,
                      online identifier, Internet Protocol address, email
                      address, account name.
                    </p>
                  </td>
                  <td valign="top">
                    <p>YES</p>
                  </td>
                </tr>
                <tr>
                  <td valign="top">
                    <p>
                      Personal information categories listed in the California
                      Customer Records statute (Cal. Civ. Code § 1798.80(e)).
                    </p>
                    <p>
                      <br />
                    </p>
                  </td>
                  <td valign="top">
                    <p>
                      A name, signature, Social Security number, physical
                      characteristics or description, address, telephone number,
                      passport number, driver&apos;s license or state
                      identification card number, insurance policy number,
                      education, employment, employment history, bank account
                      number, credit card number, debit card number, or any
                      other financial information, medical information, or
                      health insurance information.
                    </p>
                  </td>
                  <td valign="top">
                    <p>YES</p>
                  </td>
                </tr>
                <tr>
                  <td valign="top">
                    <p>Protected classification characteristics.</p>
                    <p>
                      <br />
                    </p>
                  </td>
                  <td valign="top">
                    <p>
                      Age (40 years or older), race, color, ancestry, national
                      origin, citizenship, religion or creed, marital status,
                      medical condition, physical or mental disability, sex
                      (including gender, gender identity, gender expression,
                      pregnancy or childbirth and related medical conditions),
                      sexual orientation, veteran or military status, genetic
                      information (including familial genetic information).
                    </p>
                  </td>
                  <td valign="top">
                    <p>YES</p>
                  </td>
                </tr>
                <tr>
                  <td valign="top">
                    <p>Commercial information.</p>
                  </td>
                  <td valign="top">
                    <p>
                      Records of personal property, products or services
                      purchased, obtained, or considered, or other purchasing or
                      consuming histories or tendencies.
                    </p>
                  </td>
                  <td valign="top">
                    <p>NO</p>
                  </td>
                </tr>
                <tr>
                  <td valign="top">
                    <p>Personal Records</p>
                  </td>
                  <td valign="top">
                    <p>
                      Records containing Personal Information or other
                      Identifiers including but not limited to, birth
                      certificates, vaccination records, medical examination
                      records.
                    </p>
                  </td>
                  <td valign="top">
                    <p>YES</p>
                  </td>
                </tr>
                <tr>
                  <td valign="top">
                    <p>Biometric information.</p>
                  </td>
                  <td valign="top">
                    <p>
                      Genetic, physiological, behavioral, and biological
                      characteristics, or activity patterns used to extract a
                      template or other identifier or identifying information,
                      such as, fingerprints, faceprints, and voiceprints, iris
                      or retina scans, keystroke, gait, or other physical
                      patterns, and sleep, health, or exercise data.
                    </p>
                  </td>
                  <td valign="top">
                    <p>NO</p>
                  </td>
                </tr>
                <tr>
                  <td valign="top">
                    <p>Internet or other similar network activity.</p>
                  </td>
                  <td valign="top">
                    <p>
                      Browsing history, search history, information on a
                      consumer&apos;s interaction with a website, application,
                      or advertisement.
                    </p>
                  </td>
                  <td valign="top">
                    <p>YES</p>
                  </td>
                </tr>
                <tr>
                  <td valign="top">
                    <p>Geolocation data.</p>
                  </td>
                  <td valign="top">
                    <p>Physical location or movements.</p>
                  </td>
                  <td valign="top">
                    <p>NO</p>
                  </td>
                </tr>
                <tr>
                  <td valign="top">
                    <p>Sensory data.</p>
                  </td>
                  <td valign="top">
                    <p>
                      Audio, electronic, visual, thermal, olfactory, or similar
                      information.
                    </p>
                  </td>
                  <td valign="top">
                    <p>NO</p>
                  </td>
                </tr>
                <tr>
                  <td valign="top">
                    <p>Professional or employment-related information.</p>
                  </td>
                  <td valign="top">
                    <p>
                      Current or past job history or performance evaluations.
                    </p>
                  </td>
                  <td valign="top">
                    <p>NO</p>
                  </td>
                </tr>
                <tr>
                  <td valign="top">
                    <p>Non-public education information.</p>
                  </td>
                  <td valign="top">
                    <p>
                      Education records directly related to a student maintained
                      by an educational institution or party acting on its
                      behalf, such as grades, transcripts, class lists, student
                      schedules, student identification codes, student financial
                      information, or student disciplinary records.
                    </p>
                  </td>
                  <td valign="top">
                    <p>NO</p>
                  </td>
                </tr>
                <tr>
                  <td valign="top">
                    <p>Inferences drawn from other Personal Information.</p>
                  </td>
                  <td valign="top">
                    <p>
                      Profile reflecting a person&apos;s preferences,
                      characteristics, psychological trends, predispositions,
                      behavior, attitudes, intelligence, abilities, and
                      aptitudes.
                    </p>
                  </td>
                  <td valign="top">
                    <p>NO</p>
                  </td>
                </tr>
              </tbody>
            </table>
            <br />
            <p>
              We obtain the categories of Personal Information listed above from
              the following categories of sources:
            </p>
            <ul>
              <li>
                <u>Directly from you</u>. For example, when you:
                <ul>
                  <li>register yourself with the Website;</li>
                  <li>sign up for a plan;</li>
                  <li>filling in and submitting forms on our Services; or</li>
                  <li>communicate with us, such as request information.</li>
                </ul>
              </li>
              <li>
                <u>Indirectly from you</u>. For example, through information we
                collect from you in the course of providing our Services to you
                or information provided by an Administrator or third party,
                including but not limited to, parents, coaches, and team
                managers.
              </li>
              <li>
                <u>Directly and indirectly from activity on our Website</u>. For
                example, from website usage details that are collected
                automatically. For more information on automatic information
                collection, please review our{" "}
                <a href={cookiesPath}>Cookie Policy</a>.
              </li>
            </ul>
            <p>
              The information that you provide in each case will vary. In some
              cases, you may be able to provide Personal Information via email
              or free text boxes, such as contacting the Company to request
              further information. When providing your Personal Information,
              please provide only relevant information and do not provide
              unnecessary sensitive information, such as Social Security
              numbers, credit card information, or other sensitive personal
              data, unless required for our Services.
              <br />
              <br />
              Additionally, we may ask you to create a username and password
              that should only be known to you. When you provide this
              information to us, you are no longer anonymous. Moreover, we may
              receive information about you from other sources and add it to the
              information you have provided to us.
            </p>
            <p>
              <strong>4. How We Use Your Information</strong>
            </p>
            <p>
              The information we gather and that you provide is collected to
              provide you information and the Services you request, in addition
              to various other purposes, including, but not limited to:
            </p>
            <ul>
              <li>presenting our Services and its contents to you;</li>
              <li>
                providing you with information, products, or services that you
                request from us;
              </li>
              <li>
                fulfilling any other purpose for which you provide information;
              </li>
              <li>security, credit or fraud prevention purposes;</li>
              <li>providing you with effective customer service;</li>
              <li>
                providing you with a personalized experience when you use this
                Website;
              </li>
              <li>
                carrying out our obligations and enforcing our rights arising
                from any contracts entered into between us and you, including
                for billing and collection;
              </li>
              <li>
                contacting you with special offers and other information we
                believe will be of interest to you (in accordance with any
                privacy preferences you have expressed to us);
              </li>
              <li>
                contacting you with information and notices related to your use
                of this Website;
              </li>
              <li>
                inviting you to participate in surveys and providing feedback to
                us (in accordance with any privacy preferences you have
                expressed to us);
              </li>
              <li>better understanding your needs and interests;</li>
              <li>
                improving the content, functionality, and usability of this
                Website;
              </li>
              <li>improving our products and services;</li>
              <li>improving our marketing and promotional efforts; and</li>
              <li>
                any other purpose identified in an applicable privacy notice,
                click-through agreement, or other agreement between you and us.
              </li>
            </ul>
            <p>
              <strong>5. How We Share Your Information</strong>
            </p>
            <p>
              We do not sell or lease your Personal Information to any third
              party. We may share your Personal Information by disclosing it to
              a third party for a business purpose. When we disclose your
              Personal Information, we enter into a contract that describes the
              purpose and requires the recipient to both keep that Personal
              Information confidential and not use it for any purpose except for
              the purposes set forth in the contract.
            </p>
            <p>
              In the preceding 12 months, we have disclosed the following
              categories of Personal Information for one or more business
              purposes:
            </p>
            <ul>
              <li>Identifiers;</li>
              <li>Personal Records; and</li>
              <li>
                Personal information categories listed in the California
                Customer Records statute (Cal. Civ. Code § 1798.80(e)).
              </li>
            </ul>
            <p>
              We disclose your Personal Information for a business purpose to
              the following categories of third parties:
            </p>
            <ul>
              <li>Our affiliates; and</li>
              <li>
                Third-party vendors who provide services that enhance our
                Website, products and services to you (such as vendors for
                credit card processing, advertising and marketing, and customer
                support).
              </li>
            </ul>
            <p>
              Except as described in this Policy, we will not share your
              information with third parties without your notice and consent,
              unless it is under one of the following circumstances:
            </p>
            <ul>
              <li>
                Legal Reasons.
                <ul>
                  <li>
                    We believe that disclosure is reasonably necessary to comply
                    with any applicable law, regulation, subpoena, or court
                    order;
                  </li>
                  <li>
                    To respond to duly authorized information requests from law
                    enforcement or other governmental authorities;
                  </li>
                  <li>
                    To enforce our agreements, policies, or the Terms of Use;
                  </li>
                  <li>
                    To investigate and prevent security threats, fraud, or other
                    malicious activity; or
                  </li>
                  <li>
                    To respond to an emergency that we believe in good faith
                    requires us to disclose such information to assist in
                    preventing the death or serious bodily injury of any person
                    or Company employee.
                  </li>
                </ul>
              </li>
              <li>
                Sale of Business or Merger. There are circumstances where the
                Company may decide to buy, sell, or reorganize its business in
                selected countries. Under these circumstances, it may be
                necessary to share or receive Personal Information with
                prospective or actual partners or affiliates. In such
                circumstances, the Company will ensure your information is used
                in accordance with this Policy.
              </li>
            </ul>
            <p>
              <strong>
                6. Your Choices and Selecting Your Privacy Preferences
              </strong>
            </p>
            <p>
              We want to provide you with relevant information that you have
              requested. When possible, we will always provide options as to
              what information we collect and how you can manage any preferences
              that pertains to such information. We have created mechanisms to
              provide you with the following control over your information:
            </p>
            <ul>
              <li>
                <strong>Tracking Technologies and Advertising.</strong> You can
                set your browser to refuse all or some browser cookies, or to
                alert you when cookies are being sent. If you disable or refuse
                cookies, please note that some parts of this site may then be
                inaccessible or not function properly. For more information
                about the cookies we use, please review our{" "}
                <a href={cookiesPath}>Cookie Policy</a>.
              </li>
              <li>
                <strong>
                  Disclosure of Your Information for Third-Party Advertising
                </strong>
                . If you do not want us to share your Personal Information with
                unaffiliated or non-agent third parties for promotional
                purposes, you can opt-out by sending us an email stating your
                request to{" "}
                <a href="mailto:contact@jerseywatch.com">
                  contact@jerseywatch.com
                </a>
                .
              </li>
              <li>
                <strong>Promotional Offers from the Company.</strong> If you do
                not wish to have your email address or other contact information
                used by the Company to promote our own products or services, you
                can send us an email stating your request to{" "}
                <a href="mailto:contact@jerseywatch.com">
                  contact@jerseywatch.com
                </a>
                . If we have sent you a promotional email, you may unsubscribe
                from future email distributions by clicking the appropriate link
                within the promotion email.
              </li>
              <li>
                <strong>Opt Out of Interest-Based Advertising.</strong> To opt
                out of interest-based advertising, go to{" "}
                <a href="http://optout.networkadvertising.org/?c=1">
                  http://optout.networkadvertising.org/?c=1
                </a>
                .
              </li>
            </ul>
            <p>
              <strong>
                7. Accuracy and Access to Your Personal Information
              </strong>
            </p>
            <p>
              We strive to maintain and process your information accurately. We
              have processes in place to maintain all of our information in
              accordance with relevant data governance frameworks and legal
              requirements. We employ technologies designed to help us maintain
              information accuracy on input and processing.
            </p>
            <p>
              Where we can provide you access to your Personal Information in
              our possession, we will always ask you for a username and password
              to help protect your privacy and security. We recommend that you
              keep your password safe, that you change it periodically, and that
              you do not disclose it to any other person or allow any other
              person to use it.
            </p>
            <p>
              To view and change the Personal Information that you have provided
              to us, you can log in to your account and follow the instructions
              on that webpage, or contact us directly for assistance.
            </p>
            <p>
              <strong>8. Information of Minors</strong>
            </p>
            <p>
              We do not knowingly collect or solicit any information from
              individuals under the age of 13 or knowingly allow such persons to
              register with the Services. While we may collect certain
              information about minors under 13 that a parent or organization
              provides to use our Service, the Service and its content are not
              directed at minors under the age of 13. We do not target the
              Website to minors, and would not expect them to be engaging with
              our Website or Services. We encourage parents and guardians to
              provide adequate protection measures to prevent minors from
              providing information unwillingly on the internet. If we are aware
              of any Personal Information that we have collected about minors
              under 13, we will take steps to securely remove it from our
              systems.
            </p>
            <p>
              <strong>9. Jersey Watch Administrator Sites</strong>
            </p>
            <p>
              We welcome Administrators to use our Services to create accounts,
              online registrations, and websites (collectively “Sites”) on the
              Jersey Watch Website. In addition, we may use your Site(s) in
              advertising campaigns and other promotions. We may or may not use
              your name in connection with such use, and we may or may not seek
              your consent before using the Site for such purposes. Therefore,
              you should have no expectation of privacy with respect to your
              Site(s) on or through this Website. You should not submit any
              content you do not wish to make available to the general public,
              and you must take special care to make sure your Sites comply with
              our <a href={termsPath}>Terms of Use</a> or other applicable
              agreements. All terms and conditions of our Terms of Use apply to
              Sites on or through this Website. In particular, your Sites must
              not violate the privacy or other rights of others. You may not use
              false identifying information or contact information, impersonate
              any person or entity, or otherwise mislead us as to the origin of
              any Site. You can opt out of having your Site(s) featured by
              contacting{" "}
              <a href="mailto:contact@jerseywatch.com">
                contact@jerseywatch.com
              </a>
              . This Section does not affect any rights you may have under
              applicable data protection laws.
            </p>
            <p>
              <strong>10. End Users&apos; Personal Information</strong>
            </p>
            <p>
              Administrators who have created a Site using our Services are
              solely responsible for what they do with the Personal Information
              they collect, directly or through Jersey Watch about their End
              Users. This Section 10 is directed to such individuals.
            </p>
            <ol style={{ listStyleType: "lower-alpha" }}>
              <li>
                <u>Administrators&apos; Relationship with End Users</u>.<br />
                <br />
                If you are one of our Administrators, you may collect Personal
                Information about your End Users. For example, you may ask your
                End Users (e.g., parents or guardians of a player) to provide
                you with their own or their player&apos;s name, address, email
                address, date of birth, gender, and payment information when
                completing a registration on your Site.
                <br />
                <br />
                You are solely responsible for complying with any laws and
                regulations that apply to your collection and use of your End
                Users&apos; information, including Personal Information you
                collect about them or their player(s) from us or using Jersey
                Watch functionality or cookies or similar technologies.
                <br />
                <br />
                By creating a Site, Administrators are responsible for complying
                with all the terms set forth in this Policy and applying such
                terms to their own Sites. The Company cannot provide legal
                advice to Administrators or their End Users, however we do
                recommend that all Administrators publish and maintain clear and
                comprehensive privacy policies on their Sites in accordance with
                any applicable laws and regulations, and that all End Users
                carefully read those policies and make sure that they understand
                and, to the extent required by applicable law, consent to them.
                Nothing in this Privacy Policy or the Terms of Use prohibits any
                Administrator from publishing its own privacy policy on its
                respective Site.
                <br />
                <br />
                The Company shall not be liable for your relationship with your
                End Users or how you collect, use, and share Personal
                Information about them on our Website (even if you collect it
                from us or using Jersey Watch functionality or cookies or
                similar technologies). Additionally, we will not provide you
                with any legal advice regarding such matters.
                <br />
                Additionally as part of the Services, Administrators may have
                the opportunity to send End Users “SMS” or text messages,
                pre-recorded voice messages or auto dialed phone calls
                (“Messages”). Administrators are solely responsible for
                obtaining consent from End Users to send Messages and honoring
                End Users&apos; opt-out rights regarding the Messages. As an
                Administrator, you also represent and warrant that you will not
                use the Services to call or message any email address or
                telephone number (including, without limitation, any mobile
                telephone number) using an autodialer or otherwise in violation
                of the Telephone Consumer Protection Act (“TCPA”) or other
                applicable laws.
                <br />
                <br />
              </li>
              <li>
                <u>End User payment information</u>
                <span>.</span>
                <br />
                <br />
                Your Site may require End Users&apos; payment information. Such
                information may be processed via third-party eCommerce payment
                processors, such as Stripe, with which you integrate your Jersey
                Watch account, in accordance with such eCommerce payment
                processors&apos; terms and policies. We transmit your End
                Users&apos; complete payment information when they initially
                provide or update it only so that we can pass it along to the
                eCommerce payment processors. We do NOT collect or store your
                End Users&apos; payment information other than the information
                needed to confirm such payments have been successfully processed
                by the third-party eCommerce payment processor.
              </li>
            </ol>
            <p>
              <strong>11. Third-party Websites</strong>
            </p>
            <p>
              This Policy does not apply to websites or other domains that are
              maintained or operated by third parties or our affiliates. Our
              Website may link to third-party websites and services. For
              example, if you click on an advertisement on this Website, you may
              be taken to another website that we do not control. These links
              are not endorsements of these websites, and this Policy does not
              extend to them. Because this Policy is not enforced on these
              third-party websites, we encourage you to read any posted privacy
              policy of the third-party website before using the service or
              website and providing any information.
            </p>
            <p>
              <strong>12. Your California Rights</strong>
            </p>
            <ol style={{ listStyleType: "lower-alpha" }}>
              <li>
                <u>Shine the Light law</u>. Pursuant to California Civil Code
                Section 1798.83, we will not disclose or share your Personal
                Information with third parties for the purposes of third-party
                marketing to you without your prior consent.
              </li>
              <li>
                <u>Do Not Track Signals</u>. Other than as disclosed in this
                Policy, the Website does not track users over time and across
                third-party websites to provide targeted advertising. Therefore,
                the Website does not operate any differently when it receives Do
                Not Track (“DNT”) signals from your internet web browser.
              </li>
              <li>
                <u>WE DO NOT SELL YOUR PERSONAL INFORMATION</u>. If we ever
                decide to sell Personal Information, we will update you via this
                Policy and include a link entitled “Do Not Sell My Personal
                Information,” to provide you with an opportunity to opt out of
                sales of your Personal Information.
              </li>
              <li>
                <u>California Consumer Rights</u>. If you are a California
                “consumer,” as defined by the California Consumer Privacy Act of
                2018 (the “Act”), please be advised that Digital Edge Sport,
                Inc. d/b/a Jersey Watch does not currently meet the regulatory
                thresholds of a “business” as that term is defined under the Act
                and therefore is not required to comply with the Act.
                Regardless, all Personal Information will be processed in
                accordance with this Policy.
              </li>
            </ol>
            <p>
              <strong>
                13. For Website Users Outside of the United States and Canada
              </strong>
            </p>
            <p>
              Our Website is designed for use by individuals in the United
              States and Canada only. We do not warrant or represent this Policy
              or the Website&apos;s use of your Personal Information complies
              with the laws of any other jurisdiction. Furthermore, to provide
              you with our services, we may store, process, and transmit
              information in the United States, Canada, and other locations
              around the world, including countries that may not have the same
              privacy and security laws as yours. Regardless of the country in
              which such information is stored, we will process your Personal
              Information in accordance with this Policy.
            </p>
            <p>
              <strong>14. Personal Information Deletion Request</strong>
            </p>
            <p>
              You may request that we delete your Personal Information that we
              collected from you and retained, subject to certain exceptions, by
              contacting us at contact@jerseywatch.com with your name and
              “DELETION OF PERSONAL INFORMATION REQUEST” in the email subject
              line and providing your name, relevant account username (if
              applicable) and stating your request for deletion of your Personal
              Information and other information linked to your Jersey Watch
              account. Once we receive and confirm your verifiable consumer
              request, we will delete (and direct our service providers to
              delete) your Personal Information from our records, unless an
              exception applies. Where applicable, we will ensure such changes
              are shared with trusted third parties. All deletion requests shall
              be handled in accordance with Jersey Watch&apos;s data deletion
              practices which are located at:
              <a href="https://www.jerseywatch.com/data-deletion/">
                https://www.jerseywatch.com/data-deletion/
              </a>
            </p>
            <p>
              <strong>15. Safeguarding the Information We Collect</strong>
            </p>
            <p>
              We use reasonable technical, administrative, and physical
              safeguards in order to protect your Personal Information against
              accidental loss and from unauthorized access, use, alteration, and
              disclosure. However, we can never promise 100% security. Although
              we do our best to protect your personal information, we cannot
              guarantee the security of your Personal Information transmitted to
              our Services. Any transmission of Personal Information is at your
              own risk. We cannot and do not guarantee that your responses will
              not be shared. We are not responsible for circumvention of any
              privacy settings or security measures contained on the Services.
              As an Administrator or General User, you have a responsibility, as
              well, to safeguard your information through the proper use and
              security of any online credentials used to access your Personal
              Information, such as a username and password. If you believe your
              credentials have been compromised, please change your password.
              Please also notify us of any actual or suspected unauthorized use.
            </p>
            <p>
              <strong>16. Changes to this Policy</strong>
            </p>
            <p>
              This Policy describes our current policies and practices with
              regard to the information we collect through this Website. We are
              continually improving and adding to the features and functionality
              of this Website along with the products and services we offer
              through this Website. If we make any changes to this Policy, a
              revised Policy will be posted on this webpage and the date of the
              change will be reported in the “Last Revised” block above. You can
              get to this page from any of our webpages by clicking on the
              “Privacy Policy” link (usually at the bottom of the screen).
            </p>
            <p>
              <strong>17. How to Contact Us</strong>
            </p>
            <p>
              We value your opinions and welcome your feedback. To ask questions
              or comment about this Policy or your Personal Information, please
              contact us by email at{" "}
              <a href="mailto:contact@jerseywatch.com">
                contact@jerseywatch.com
              </a>
              .
            </p>
          </div>
        </div>
        <style jsx>{`
          p {
            margin-top: 12px;
          }
        `}</style>
      </Container>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return { props: {} };
};

export default Page;
